/**
 * Request Context for SSR
 *
 * This module provides a way to access request information during server-side rendering.
 * On the server, AsyncLocalStorage (set up in workers/app.ts and exposed via globalThis)
 * isolates state per-request in Cloudflare Workers, preventing cross-request leakage.
 */

// Access the AsyncLocalStorage instance created in workers/app.ts.
// Returns undefined on the client (no ALS available).
function getServerStore(): { url: URL | null } | undefined {
	return (globalThis as any).__requestALS?.getStore() ?? undefined;
}

export function setRequestContext(request: Request) {
	const store = getServerStore();
	if (store) {
		store.url = new URL(request.url);
	}
}

export function getRequestUrl(): URL | null {
	return getServerStore()?.url ?? null;
}

export function clearRequestContext() {
	const store = getServerStore();
	if (store) {
		store.url = null;
	}
}

/**
 * Check if the current request is from a local development environment
 */
export function isLocalRequest(): boolean {
	const currentUrl = getRequestUrl();
	if (!currentUrl) return false;
	const hostname = currentUrl.hostname;
	const port = currentUrl.port;
	// Check for local hostnames OR common local dev ports (3000, 5173, etc.)
	return (
		hostname.includes(".local") ||
		hostname.includes("localhost") ||
		hostname === "127.0.0.1" ||
		(port === "3000" && (hostname === "127.0.0.1" || hostname === "localhost"))
	);
}

/**
 * Check if the current request is from a staging environment
 */
export function isStagingRequest(): boolean {
	const currentUrl = getRequestUrl();
	if (!currentUrl) return false;
	const hostname = currentUrl.hostname;
	return hostname.includes("staging.");
}

/**
 * Get the appropriate API URL based on the current request context
 */
export function getApiUrlFromRequest(): string | null {
	const currentUrl = getRequestUrl();
	if (!currentUrl) {
		return null;
	}

	if (isLocalRequest()) {
		return "https://www.123-flowers.local/api";
	} else if (isStagingRequest()) {
		return "https://staging.123-flowers.co.uk/api";
	}
	return "https://www.123-flowers.co.uk/api";
}
